/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { StoryCard, StoryCardsEventFeedArgs, storyCardApi } from '../services';
import {
  addCard,
  addComment,
  deleteComment,
  editComment,
  hideComment,
  highlightComment,
  loadMoreComments,
  reactOnCard,
  reactOnComment,
} from './common';
import {
  addCommentReducer,
  deleteCommentReducer,
  editCommentReducer,
  hideCommentReducer,
  highlightCommentReducer,
  reactOnCardReducer,
  reactOnCommentReducer,
} from './extraReducers';

interface EventDiscussionFeedState {
  isFetched: boolean;
  eventDiscussionFeed: StoryCard[];
  page: number;
  hasNextPage: boolean;
  totalItems: number;
  currentEventId: number | null;
}

const initialState: EventDiscussionFeedState = {
  isFetched: false,
  eventDiscussionFeed: [],
  page: 0,
  hasNextPage: false,
  totalItems: 0,
  currentEventId: null,
};

const eventDiscussionFeedHandler = async (args: StoryCardsEventFeedArgs, { dispatch }: any) =>
  dispatch(storyCardApi.endpoints.storyCardsEventFeed.initiate({ ...args })).unwrap();

export const getEventDiscussionFeed = createAsyncThunk(
  'eventDiscussionFeed',
  eventDiscussionFeedHandler
);

export const loadMoreEventDiscussionFeed = createAsyncThunk(
  'eventDiscussionFeed/loadMore',
  eventDiscussionFeedHandler
);

const eventDiscussionFeedSlice = createSlice({
  name: 'eventDiscussionFeed',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getEventDiscussionFeed.pending, (state, action) => {
        state.isFetched = false;
        state.currentEventId = action.meta.arg.eventId;
      })
      .addCase(getEventDiscussionFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage, totalItems } = pageInfo;

        state.isFetched = true;
        state.eventDiscussionFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
        state.totalItems = totalItems;
      })
      .addCase(loadMoreEventDiscussionFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage, totalItems } = pageInfo;

        state.eventDiscussionFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
        state.totalItems = totalItems;
      })
      .addCase(addCard, (state, action) => {
        if (action.payload.eventId === state.currentEventId) {
          state.eventDiscussionFeed.unshift(action.payload);
          state.totalItems++;
        }
      })
      .addCase(reactOnCard.fulfilled, (state, action) => {
        const { cardId, reaction, reactions } = action.payload;

        const card = state.eventDiscussionFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCardReducer({ card, reaction, reactions });
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { itemId, comment } = action.payload;

        const card = state.eventDiscussionFeed.find(({ id }) => id === itemId);

        if (!card) {
          return;
        }

        addCommentReducer({ card, comment });
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.eventDiscussionFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        editCommentReducer({ card, comment });
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.eventDiscussionFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        hideCommentReducer({ card, comment });
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.eventDiscussionFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        highlightCommentReducer({ card, comment });
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { deleted, cardId, commentId, parentId, repliesCount } = action.payload;

        if (!deleted) {
          return;
        }

        const card = state.eventDiscussionFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        deleteCommentReducer({ card, commentId, parentId, repliesCount });
      })
      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, cardId, commentId } = action.payload;

        const card = state.eventDiscussionFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCommentReducer({ card, commentId, reactions });
      })
      .addCase(loadMoreComments.fulfilled, (state, action) => {
        const { itemId, items, parentId } = action.payload;

        const card = state.eventDiscussionFeed.find(({ id }) => id === itemId);

        if (!card) {
          return;
        }

        const { comments } = card;

        if (parentId) {
          const replies = comments.find(({ id }) => id === parentId)?.replies;

          items.forEach((reply) => {
            if (!replies?.find(({ id }) => id === reply.id)) {
              replies?.push(reply);
            }
          });
        } else {
          comments.push(...items.filter(({ id }) => !comments.map(({ id }) => id).includes(id)));
        }
      });
  },
});

export default eventDiscussionFeedSlice.reducer;
